import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { admins, WRAPPED_PUBLIC, WRAPPED_SEASON, wrappedPromoActive } from "../Constants";
import { getWrappedEligible } from "../services/WrappedService";
import { track } from "../utils/track";
import styles from "./WrappedPromo.module.css";

// Eye-catching "see this player's Season Wrapped" banner on the player page (launch mechanics,
// tasks/wrapped-design.md).
// Gated to beta+admins while WRAPPED_PUBLIC is false, exactly like the minigame dark launches.
// Also only shown when THIS player actually has an eligible Season Wrapped — no point pointing at
// an empty one for someone who hasn't played this season.
//
// Time-boxed like the nudge: after WRAPPED_PROMO_LAST_DAY it renders nothing (and skips the
// eligibility request), so it stops competing with the running season on every player page. The
// /wrapped page itself keeps working — only this promo goes away.

const WrappedProfileBanner = ({ steamId, playerName }: { steamId?: string; playerName?: string }) => {
  const { t } = useTranslation("pages");
  const { user, isBeta } = useContext(AuthContext);
  const isAdmin = !!user && admins.includes(user.steamID);
  const canPreview = (WRAPPED_PUBLIC || isBeta || isAdmin) && wrappedPromoActive();

  const [eligible, setEligible] = useState(false);
  useEffect(() => {
    let active = true;
    if (canPreview && steamId) {
      getWrappedEligible(steamId, WRAPPED_SEASON).then((ok) => { if (active) setEligible(ok); });
    } else {
      setEligible(false);
    }
    return () => { active = false; };
  }, [canPreview, steamId]);

  if (!canPreview || !steamId || !eligible) return null;

  return (
    <Link
      className={styles.banner}
      to={`/wrapped/${WRAPPED_SEASON}/${steamId}`}
      onClick={() => track("wrapped-banner-click", { season: WRAPPED_SEASON, steamId })}
    >
      <span className={styles.bannerShine} />
      <span className={styles.bannerIcon}>🎁</span>
      <span className={styles.bannerText}>
        <span className={styles.bannerKicker}>{t("wrapped.kicker")}</span>
        <span className={styles.bannerTitle}>{t("wrapped.banner.title", { name: playerName || "", season: WRAPPED_SEASON })}</span>
      </span>
      <span className={styles.bannerArrow}>→</span>
    </Link>
  );
};

export default WrappedProfileBanner;
